import {ReactNode, useContext} from 'react';

import {useTranslation} from 'react-i18next';

import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import TagOutlinedIcon from '@mui/icons-material/TagOutlined';
import {Box, Grid, SxProps, Typography} from '@mui/material';
import {SxProps as MuiSxProps} from '@mui/material/styles';

import {LanguageContext} from '../../../../../../../contexts/LanguageContext/LanguageContext';
import {accent, greenColors, orangeColors} from '../../../../../../../layouts/theme/designSystem';
import {calculateDuration, transformDate} from '../../../../../../../utils/dateUtils/dateFormattingUtils';
import {Survey} from '../../../types/SurveyTypes';

interface Props {
  survey: Survey | undefined;
  state?: string | null;
  typographyStyles?: SxProps;
  asBadge?: boolean;
  hideMetadata?: boolean;
}

export default function DateRangeDetails({survey, state, typographyStyles, asBadge, hideMetadata = false}: Props) {
  const {t} = useTranslation();
  const currentLanguage = useContext(LanguageContext);

  const handleEndDate = () => {
    switch (state) {
      case 'current':
        return survey?.endsAt ? transformDate(survey.endsAt, 'numeric') : '';
      case 'upcoming':
        return survey?.endsAt ? transformDate(survey.endsAt, 'numeric') : '';
      case 'previous':
        return survey?.endedAt ? transformDate(survey.endedAt, 'numeric') : '';
      default:
        return '';
    }
  };

  const periodLabel = survey
    ? t('companySurveys.details.period', {
      startDate: transformDate(survey?.startedAt || survey?.startsAt || '', 'numeric'),
      endDate: handleEndDate(),
    })
    : '';
  const statusLabel =
    state === 'current'
      ? t('companySurveys.stats.current')
      : state === 'previous'
        ? t('companySurveys.stats.closed')
        : '';
  const runningLabel =
    state === 'current' && survey?.startedAt
      ? t('companySurveys.details.running', {
        activeFrom: calculateDuration(survey.startedAt, new Date(), currentLanguage),
        startDate: transformDate(survey.startedAt, 'numeric'),
      })
      : '';
  const badgeLabel = [statusLabel, periodLabel].filter(Boolean).join(' - ');
  const getCultureAnalysisStatusLabel = (status: string) => {
    const translationKey = `companySurveys.details.cultureAnalysisStatus.${status}`;
    const translation = t(translationKey);

    return translation === translationKey ? status : translation;
  };
  const pillStyles: MuiSxProps = {
    alignItems: 'center',
    backgroundColor: orangeColors[7],
    border: `0.5px solid ${orangeColors[3]}`,
    borderRadius: '8px',
    color: greenColors[700],
    display: 'inline-flex',
    gap: '6px',
    padding: '4px 10px',
    width: 'auto',
  };

  const renderBadgePill = (icon: ReactNode, label: string, key: string) => (
    <Box key={key} sx={pillStyles}>
      {icon}
      <Typography
        sx={{
          color: 'inherit',
          fontSize: '12px',
          lineHeight: 1.4,
          ...typographyStyles,
        }}
      >
        {label}
      </Typography>
    </Box>
  );

  if (asBadge) {
    return survey ? (
      <Grid
        container
        sx={{
          alignItems: 'center',
          gap: 1,
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <Grid item>
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            {renderBadgePill(<CalendarTodayOutlinedIcon sx={{fontSize: 13}} />, badgeLabel, 'period')}
            {!hideMetadata && survey.surveyCode
              ? renderBadgePill(<TagOutlinedIcon sx={{fontSize: 13}} />, survey.surveyCode, 'survey-code')
              : null}
            {!hideMetadata && survey.cultureAnalysis?.status
              ? renderBadgePill(
                <AssessmentOutlinedIcon sx={{fontSize: 13}} />,
                getCultureAnalysisStatusLabel(survey.cultureAnalysis.status),
                'culture-analysis-status'
              )
              : null}
          </Box>
        </Grid>
        {runningLabel ? (
          <Grid item>
            {renderBadgePill(
              <FiberManualRecordIcon
                sx={{
                  color: accent.icon,
                  fontSize: '10px',
                }}
              />,
              runningLabel,
              'running'
            )}
          </Grid>
        ) : null}
      </Grid>
    ) : null;
  }

  return (
    <Grid
      container
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
      }}
    >
      {survey && (
        <Typography
          variant='body1'
          sx={{
            marginTop: '3px',
            color: greenColors[700],
            ...typographyStyles,
          }}
        >
          {periodLabel}
        </Typography>
      )}
    </Grid>
  );
}
